import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import Mobile1 from '../../assets/landing_page_images/mobile1.webp';
import Mobile2 from '../../assets/landing_page_images/mobile2.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import { APP_URLS } from '../../helpers/url_helper';
import './DigitalWHSForms.css';

const INSPECTION_USES = [
  {
    title: 'Site inspections',
    text: 'complete inspection forms from a phone or other mobile device.',
    icon: 'clipboard',
  },
  {
    title: 'Pre-start checklists',
    text: 'link forms to a project for consistent records.',
    icon: 'list',
  },
  {
    title: 'Hazard and incident reports',
    text: (
      <>
        Hazard reports allow workers to submit hazard information from the field, while incident reports capture an immediate digital record. For a broader workflow, explore{' '}
        <Link
          to={APP_URLS.INCIDENT_MANAGEMENT_SOFTWARE}
          className="section-description-link"
        >
          incident management software
        </Link>
        .
      </>
    ),
    icon: 'hazard',
  },
  {
    title: 'Site inductions',
    text: 'provide forms through a QR code.',
    icon: 'qr',
  },
  {
    title: 'Toolbox talks',
    text: 'use QR-accessed forms for site-based records.',
    icon: 'users',
  },
  {
    title: 'Auditor evidence',
    text: 'export completed forms as PDFs when a documented record is required.',
    icon: 'pdf',
  },
];

const FEATURE_CARDS = [
  {
    title: 'Build Forms From Templates',
    icon: 'form',
    paragraphs: [
      <>
        Administrators can create forms from company templates or build new forms using sections, fields, signatures and instruction files. Forms can also be activated, deactivated or deleted as requirements change. The{' '}
        <Link
          to={APP_URLS.AI_COMPLIANCE_ASSISTANT}
          className="section-description-link"
        >
          AI Compliance Assistant
        </Link>
        {' '}can also help teams find information in their uploaded compliance documents.
      </>,
    ],
  },
  {
    title: 'Give Workers QR and Mobile Access',
    icon: 'qr',
    paragraphs: [
      'QR codes can be printed or downloaded and used to provide quick access to forms. Workers can complete active forms through mobile access without needing to work from a desktop.',
    ],
  },
  {
    title: 'Capture Signatures and Dynamic Information',
    icon: 'signature',
    paragraphs: [
      'Forms support dynamic fields and signature capture. They can also draw information from employees, projects, sites, assets and tasks, helping reduce repeated data entry.',
    ],
  },
  {
    title: 'Keep a Submission History',
    icon: 'history',
    paragraphs: [
      <>
        Completed forms are retained with submission history and record details. Forms can be linked to sites, projects and employees so compliance records remain connected to the relevant part of the business, including{' '}
        <Link
          to={APP_URLS.CONTRACTOR_MANAGEMENT}
          className="section-description-link"
        >
          contractor management
        </Link>
        {' '}workflows where applicable.
      </>,
    ],
  },
  {
    title: 'Export Completed Forms as PDF',
    icon: 'pdf',
    paragraphs: [
      <>
        Blank or completed forms can be exported as PDFs. This can be useful when teams need to provide documented evidence for internal reviews,{' '}
        <Link
          to={APP_URLS.WHS_COMPLIANCE_AUDIT_SOFTWARE}
          className="section-description-link"
        >
          audits
        </Link>
        {' '}or other compliance processes.
      </>,
    ],
  },
];

const WORKFLOW_STEPS = [
  {
    title: 'Create a form',
    text: 'build one from a template or create your own.',
    icon: 'form',
  },
  {
    title: 'Add the required fields',
    text: 'include instructions, signatures and relevant dynamic fields.',
    icon: 'list',
  },
  {
    title: 'Connect the form',
    text: 'link it to a site, project, employee, asset or task where appropriate.',
    icon: 'link',
  },
  {
    title: 'Publish the form',
    text: 'make it available to workers.',
    icon: 'publish',
  },
  {
    title: 'Share by QR code',
    text: 'print or download a QR code for quick site access.',
    icon: 'qr',
  },
  {
    title: 'Complete on mobile',
    text: 'workers fill out and submit the form from the field.',
    icon: 'mobile',
  },
  {
    title: 'Review the record',
    text: 'view submission history and individual records.',
    icon: 'history',
  },
  {
    title: 'Export when required',
    text: 'generate a PDF of the completed form.',
    icon: 'pdf',
  },
];

const CAPABILITY_TABLE = [
  { capability: 'Form builder', value: 'Sections, fields, signatures and instruction files', icon: 'form' },
  { capability: 'Templates', value: 'Company forms and template library', icon: 'folder' },
  { capability: 'QR access', value: 'Print or download QR codes for forms', icon: 'qr' },
  { capability: 'Mobile forms', value: 'Workers can browse and complete active forms', icon: 'mobile' },
  { capability: 'Dynamic fields', value: 'Data from employees, projects, sites, assets and tasks', icon: 'fields' },
  { capability: 'Form records', value: 'Submission history and record details', icon: 'history' },
  { capability: 'PDF export', value: 'Export blank or completed forms', icon: 'pdf' },
  { capability: 'Form linking', value: 'Connect forms with Sites, Projects and Employees', icon: 'link' },
  { capability: 'Notifications', value: 'Notify users when forms are submitted', icon: 'bell' },
];

const SPEC_TABLE = [
  { capability: 'Product', value: 'Digital WHS Forms & QR' },
  { capability: 'Form access', value: 'Web/mobile access' },
  { capability: 'QR codes', value: 'Print and download' },
  { capability: 'Form fields', value: 'Dynamic fields' },
  { capability: 'Signatures', value: 'Signature capture' },
  { capability: 'Templates', value: 'Company forms and template library' },
  { capability: 'Linked records', value: 'Sites, Projects, Employees, Assets, Tasks' },
  { capability: 'Records', value: 'Submission history and detail view' },
  { capability: 'Export', value: 'Blank and completed PDF' },
  { capability: 'Industries', value: 'Construction, Childcare, Health, NFP, Local Government' },
  { capability: 'Trial', value: 'Free trial without card details' },
];

const FAQ_ITEMS = [
  {
    question: 'What are digital WHS forms?',
    answer:
      'Digital WHS forms are electronic workplace health and safety forms that workers can complete and submit digitally. Complimate supports form creation, mobile access, QR codes, signatures, submission records and PDF export.',
  },
  {
    question: 'Can workers complete WHS forms using a QR code?',
    answer:
      'Yes. Complimate can generate QR codes for forms that can be printed or downloaded, allowing workers to access forms from the field using mobile devices.',
  },
  {
    question: 'Can Complimate be used for safety inspections?',
    answer:
      'Yes. The platform supports inspections and audits, while its digital forms can be used for site inspections, pre-start checklists, hazard reports and other WHS records.',
  },
  {
    question: 'Can digital forms be linked to a construction site?',
    answer:
      'Yes. Forms can be linked to Sites and Projects, allowing submissions to be associated with the relevant location or project.',
  },
  {
    question: 'Can completed WHS forms be exported?',
    answer:
      'Yes. Complimate supports PDF export for both blank and completed forms.',
  },
  {
    question: 'Does Complimate offer a free trial?',
    answer:
      'The supplied product documentation states that Complimate offers a free trial without requiring card details.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/digital-whs-forms/');

const ICON_PATHS = {
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h4v4h-4zM20 14v3M14 20h3" />
    </>
  ),
  clipboard: (
    <>
      <path d="M8 4h8" />
      <path d="M9 3v3h6V3" />
      <path d="M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  list: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" />
    </>
  ),
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5" />
      <path d="M12 17h.01" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6" />
      <path d="M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  pdf: (
    <>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h3.2c1.2 0 2 .7 2 1.8s-.8 1.8-2 1.8H9v-3.6Z" />
    </>
  ),
  signature: (
    <>
      <path d="M4 18c2.4-1.2 3.8-3.6 5.4-6.2 1.8-3 3.8-5.4 8.6-5.6" />
      <path d="M4 20h16" />
      <path d="M15 8c1.4-1.6 4.2-1.6 5.4.2" />
    </>
  ),
  history: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.2 1.8" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.07.07l1.41-1.41a5 5 0 0 0-7.07-7.07L10 6" />
      <path d="M14 11a5 5 0 0 0-7.07-.07L5.52 12.34a5 5 0 0 0 7.07 7.07L14 18" />
    </>
  ),
  publish: (
    <>
      <path d="M12 19V5" />
      <path d="m7 10 5-5 5 5" />
      <path d="M5 19h14" />
    </>
  ),
  mobile: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  fields: (
    <>
      <rect x="3" y="5" width="18" height="4" rx="1.5" />
      <rect x="3" y="11" width="18" height="4" rx="1.5" />
      <rect x="3" y="17" width="12" height="4" rx="1.5" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </>
  ),
  library: (
    <>
      <path d="M4 19a2 2 0 0 0 2 2h12" />
      <path d="M6 21V5a2 2 0 0 1 2-2h10v18" />
      <path d="M10 8h6M10 12h6" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="dwf-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.form}
      </svg>
    </span>
  );
}

function QrMark() {
  return (
    <span className="dwf-qr-mark" aria-hidden="true">
      <svg viewBox="0 0 48 48" fill="none">
        <rect x="4" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2.2" />
        <rect x="30" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2.2" />
        <rect x="4" y="30" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2.2" />
        <rect x="8" y="8" width="6" height="6" fill="currentColor" />
        <rect x="34" y="8" width="6" height="6" fill="currentColor" />
        <rect x="8" y="34" width="6" height="6" fill="currentColor" />
        <rect x="24" y="24" width="6" height="6" fill="currentColor" />
        <rect x="32" y="24" width="4" height="4" fill="currentColor" />
        <rect x="24" y="34" width="4" height="8" fill="currentColor" />
        <rect x="40" y="30" width="4" height="10" fill="currentColor" />
        <rect x="32" y="40" width="8" height="4" fill="currentColor" />
      </svg>
    </span>
  );
}

function DigitalWHSForms() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="dwf-page">
        <section className="dwf-hero">
          <Container>
            <div className="dwf-hero-grid">
              <div className="dwf-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="qr" />
                  Digital WHS Forms &amp; QR
                </p>
                <h1 className="seo-h1">Digital WHS Forms &amp; QR</h1>
                <p className="seo-lead">
                  Complimate&apos;s Digital WHS Forms &amp; QR system helps
                  Australian businesses replace paper-based safety forms with
                  digital forms that workers can complete on mobile devices. Build
                  forms from templates, add dynamic fields and signatures, connect
                  forms to sites, projects, employees or assets, and use QR codes
                  for quick access in the field.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to={APP_URLS.SIGNUP}
                    className="btn-teal dwf-primary-button"
                    id="dwf_start_trial_hero"
                  >
                    Start Your Free Trial
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>

              <div className="dwf-hero-visual">
                <div className="dwf-visual-glow" aria-hidden="true" />
                <div className="dwf-product-window">
                  <div className="dwf-window-bar" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <img
                    src={isDark ? DarkDocument : LightDocument}
                    alt="Complimate digital WHS forms library with templates, records and QR access"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <aside className="dwf-qr-card" aria-hidden="true">
                  <QrMark />
                  <span className="dwf-qr-kicker">QR access</span>
                  <span className="dwf-qr-line dwf-qr-line-lg" />
                  <span className="dwf-qr-line" />
                </aside>
                <div className="dwf-float-card dwf-float-card-top" aria-hidden="true">
                  <FeatureIcon name="mobile" />
                  <span>Complete on mobile</span>
                </div>
                <div className="dwf-float-card dwf-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="signature" />
                  <span>Capture signatures</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section dwf-intro-section">
          <Container>
            <div className="dwf-intro-grid">
              <div>
                <div className="dwf-section-marker">
                  <FeatureIcon name="form" />
                </div>
                <h2 className="seo-h2">What Are Digital WHS Forms?</h2>
                <p className="seo-body">
                  Digital WHS forms are electronic versions of workplace health
                  and safety forms that can be completed, submitted and stored
                  digitally instead of relying on paper records.
                </p>
                <p className="seo-body">
                  Complimate provides two connected parts: a Forms Library for
                  administrators to create and manage forms, and Digital Forms
                  for workers to access and complete them. Forms can include
                  sections, fields, instructions and signature capture, while
                  completed submissions can be stored and exported as PDFs.
                </p>
                <p className="seo-body">
                  This makes the platform suitable for businesses that need
                  electronic safety forms, digital WHS checklists and mobile
                  compliance records across one or multiple sites, supported by{' '}
                  <Link
                    to={APP_URLS.SAFETY_MANAGEMENT_SOFTWARE}
                    className="section-description-link"
                  >
                    safety management software
                  </Link>
                  .
                </p>
                <div className="dwf-split-pills">
                  <div className="dwf-split-pill">
                    <FeatureIcon name="library" />
                    <span>Forms Library</span>
                  </div>
                  <div className="dwf-split-pill">
                    <FeatureIcon name="mobile" />
                    <span>Digital Forms</span>
                  </div>
                </div>
              </div>
              <div className="dwf-shot-frame">
                <img
                  src={isDark ? DarkDashboard : LightDashboard}
                  alt="Complimate dashboard showing digital WHS form activity and compliance records"
                  width="960"
                  height="680"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section dwf-inspect-section">
          <Container>
            <div className="dwf-inspect-intro">
              <div>
                <div className="dwf-section-marker">
                  <FeatureIcon name="clipboard" />
                </div>
                <h2 className="seo-h2">
                  How Can Complimate Be Used for Safety Inspections?
                </h2>
                <p className="seo-body">
                  Complimate can support safety inspection software use cases by
                  allowing teams to create digital inspection checklists and
                  collect records directly from the workplace.
                </p>
                <p className="seo-body dwf-label">
                  <strong>Common examples include:</strong>
                </p>
              </div>
              <div className="dwf-shot-frame dwf-shot-frame-soft">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate digital form workflow for inspections, checklists and site records"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <ul className="dwf-inspect-grid">
              {INSPECTION_USES.map((item) => (
                <li className="dwf-inspect-card" key={item.title}>
                  <FeatureIcon name={item.icon} />
                  <div>
                    <h3 className="seo-h4">{item.title}</h3>
                    <p className="seo-body mb-0">
                      {typeof item.text === 'string' ? <>— {item.text}</> : item.text}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="seo-section dwf-features-section">
          <Container>
            <div className="seo-section-header dwf-centered-heading">
              <h2 className="seo-h2">
                What Features Are Included in Digital WHS Forms?
              </h2>
            </div>
            <div className="dwf-feature-grid">
              {FEATURE_CARDS.map((item, index) => (
                <article className="dwf-feature-card" key={item.title}>
                  <div className="dwf-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph, paragraphIndex) => (
                    <p key={`${item.title}-${paragraphIndex}`} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section dwf-workflow-section">
          <Container>
            <div className="seo-section-header dwf-centered-heading">
              <h2 className="seo-h2">How Does the Digital WHS Forms System Work?</h2>
            </div>
            <div className="dwf-steps">
              {WORKFLOW_STEPS.map((step, index) => (
                <article key={step.title} className="dwf-step">
                  <div className="dwf-step-number">{index + 1}</div>
                  <FeatureIcon name={step.icon} />
                  <div>
                    <h3 className="seo-h4">{step.title}</h3>
                    <p className="seo-body mb-0">— {step.text}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="dwf-mobile-note">
              <div className="dwf-mobile-copy">
                <FeatureIcon name="mobile" />
                <p className="seo-body mb-0">
                  Workers can complete active forms through mobile access without
                  needing to work from a desktop.
                </p>
              </div>
              <div className="dwf-mobile-shots">
                <img
                  src={Mobile1}
                  alt="Complimate mobile app for completing digital WHS forms in the field"
                  width="220"
                  height="440"
                />
                <img
                  src={Mobile2}
                  alt="Complimate mobile form submission and QR access on a phone"
                  width="220"
                  height="440"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section dwf-cta-section">
          <Container>
            <div className="dwf-cta-card">
              <div className="dwf-cta-orb dwf-cta-orb-one" aria-hidden="true" />
              <div className="dwf-cta-orb dwf-cta-orb-two" aria-hidden="true" />
              <div className="dwf-cta-copy">
                <h2 className="seo-h2">
                  Make WHS Forms Easier to Complete and Track
                </h2>
                <p className="seo-body">
                  Move site inductions, inspections, checklists, hazard reports and
                  other WHS forms into a structured digital workflow with Complimate
                  Digital WHS Forms &amp; QR.
                </p>
              </div>
              <Button
                as={Link}
                to={APP_URLS.SIGNUP}
                className="btn-teal dwf-cta-button"
                id="dwf_start_trial_mid"
              >
                Start Your Free Trial
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section dwf-manage-section">
          <Container>
            <div className="seo-section-header dwf-centered-heading">
              <h2 className="seo-h2">What Can You Manage With Digital WHS Forms?</h2>
            </div>
            <span className="visually-hidden">Capability</span>
            <span className="visually-hidden">Complimate functionality</span>
            <div className="dwf-manage-grid" role="list">
              {CAPABILITY_TABLE.map((row) => (
                <article className="dwf-manage-card" role="listitem" key={row.capability}>
                  <FeatureIcon name={row.icon} />
                  <div>
                    <h3 className="seo-h4">{row.capability}</h3>
                    <p className="seo-body mb-0">{row.value}</p>
                  </div>
                </article>
              ))}
            </div>
            <p className="seo-body seo-diff-note">
              These capabilities are documented as part of Complimate&apos;s live
              Digital Forms &amp; QR functionality.
            </p>
          </Container>
        </section>

        <section className="seo-section dwf-spec-section">
          <Container>
            <div className="seo-section-header dwf-centered-heading">
              <h2 className="seo-h2">
                Complimate Digital WHS Forms: Key Specifications
              </h2>
            </div>
            <div className="seo-table-wrap dwf-spec-table">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th scope="col">Specification</th>
                    <th scope="col">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {SPEC_TABLE.map((row) => (
                    <tr key={row.capability}>
                      <th scope="row">{row.capability}</th>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="seo-body seo-diff-note">
              The supplied product documentation confirms mobile access, QR
              functionality, PDF export and the listed industry positioning.
            </p>
          </Container>
        </section>

        <section className="seo-section dwf-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default DigitalWHSForms;
